"use client";

import React from 'react';
import Link from 'next/link';
import { Check, ArrowRight } from 'lucide-react';
import { Button } from '@/app/components/ui/Button';
import { cn } from '@/lib/utils';

interface PricingSectionProps {
  className?: string;
  id?: string;
}

export function PricingSection({ className, id }: PricingSectionProps) {
  return (
    <section id={id} className={cn("py-24 px-6 bg-background", className)}>
      <div className="container mx-auto max-w-6xl">
        <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-6">
          <div>
            <h2 className="font-display text-5xl mb-4">Simple Pricing</h2>
            <p className="text-xl text-muted-foreground">Transparent fees. No hidden costs.</p>
          </div>
          <Link href="/comparison">
            <Button variant="outline" className="gap-2">
              View full comparison <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-foreground/10 divide-y md:divide-y-0 md:divide-x divide-foreground/10">

          {/* Single Will */}
          <div className="p-12 flex flex-col bg-white">
            <h3 className="font-display text-2xl mb-4">Single Will</h3>
            <div className="text-5xl font-bold mb-6">£70</div>
            <p className="text-muted-foreground mb-8 min-h-[48px]">
              Perfect for individuals who want to ensure their wishes are respected.
            </p>
            <ul className="space-y-4 mb-12 flex-1">
              <li className="flex items-center gap-3 text-sm">
                <Check className="w-4 h-4 text-teal" />
                <span>Reviewed by an expert</span>
              </li>
              <li className="flex items-center gap-3 text-sm">
                <Check className="w-4 h-4 text-teal" />
                <span>Valid in England & Wales</span>
              </li>
              <li className="flex items-center gap-3 text-sm">
                <Check className="w-4 h-4 text-teal" />
                <span>Peace of mind</span>
              </li>
            </ul>
            <Link href="/write-a-will-online?type=single" className="w-full">
              <Button variant="outline" className="w-full rounded-none">Get Started</Button>
            </Link>
          </div>

          {/* Mirror Will */}
          <div className="p-12 flex flex-col bg-teal/5 relative">
            <div className="absolute top-0 right-0 bg-teal text-white text-xs px-3 py-1 font-medium uppercase tracking-wider">
              Most Popular
            </div>
            <h3 className="font-display text-2xl mb-4">Mirror Will</h3>
            <div className="text-5xl font-bold mb-6">£100</div>
            <p className="text-muted-foreground mb-8 min-h-[48px]">
              Ideal for couples who want to mirror each other's wishes.
            </p>
            <ul className="space-y-4 mb-12 flex-1">
              <li className="flex items-center gap-3 text-sm">
                <Check className="w-4 h-4 text-teal" />
                <span>Two similar Wills</span>
              </li>
              <li className="flex items-center gap-3 text-sm">
                <Check className="w-4 h-4 text-teal" />
                <span>Reviewed by an expert</span>
              </li>
              <li className="flex items-center gap-3 text-sm">
                <Check className="w-4 h-4 text-teal" />
                <span>Valid in England & Wales</span>
              </li>
              <li className="flex items-center gap-3 text-sm">
                <Check className="w-4 h-4 text-teal" />
                <span>Peace of mind</span>
              </li>
            </ul>
            <Link href="/write-a-will-online?type=mirror" className="w-full">
              <Button variant="primary" className="w-full rounded-none">Get Started</Button>
            </Link>
          </div>

          {/* Complex Will */}
          <div className="p-12 flex flex-col bg-white">
            <h3 className="font-display text-2xl mb-4">Complex Will</h3>
            <div className="text-5xl font-bold mb-6">£250</div>
            <p className="text-muted-foreground mb-8 min-h-[48px]">
              For larger estates or complex family situations requiring advice.
            </p>
            <ul className="space-y-4 mb-12 flex-1">
              <li className="flex items-center gap-3 text-sm">
                <Check className="w-4 h-4 text-teal" />
                <span>Reviewed by an expert</span>
              </li>
              <li className="flex items-center gap-3 text-sm">
                <Check className="w-4 h-4 text-teal" />
                <span>Complex assets</span>
              </li>
              <li className="flex items-center gap-3 text-sm">
                <Check className="w-4 h-4 text-teal" />
                <span>Custom clauses</span>
              </li>
            </ul>
            <Link href="/get-in-touch" className="w-full">
              <Button variant="outline" className="w-full rounded-none">Contact Us</Button>
            </Link>
          </div>

        </div>
        
        <div className="mt-16 text-center">
          <p className="text-lg font-medium text-foreground/80">
            100% Satisfaction or your money back.
          </p>
        </div>
      </div>
    </section>
  );
}

